import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Share2, Check, Star, Shield, Award, Globe, Zap } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

interface Certificate {
  id: string;
  course_id: string;
  verification_code: string;
  issued_at: string;
  certificate_url?: string;
}

interface BadgeViewerProps {
  courseTitle: string;
  userProfile?: any;
}

export const BadgeViewer: React.FC<BadgeViewerProps> = ({ 
  courseTitle,
  userProfile 
}) => {
  const { user } = useAuth();
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (user) {
      loadCertificate();
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const loadCertificate = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setCertificate(data);
      }
    } catch (error) {
      console.error('Error loading certificate:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateBadgePNG = async () => {
    if (!certificate || !user) return;

    setDownloading(true);

    try {
      const badgeElement = document.getElementById('badge-content');
      if (!badgeElement) return;

      // Wait for any images/fonts to load
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create high-quality canvas
      const canvas = await html2canvas(badgeElement, {
        scale: 4, // Very high quality for badge
        useCORS: true,
        allowTaint: true,
        backgroundColor: 'transparent',
        width: badgeElement.scrollWidth,
        height: badgeElement.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      // Convert to PNG and download
      const link = document.createElement('a');
      link.download = `QuantumBox_AI_L1_Badge_${recipientName.replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      toast.success('Badge downloaded successfully!');
    } catch (error) {
      console.error('Error generating badge:', error);
      toast.error('Failed to download badge');
    } finally {
      setDownloading(false);
    }
  };

  const shareBadge = async () => {
    if (!certificate) return;

    const shareText = `🏆 Just earned my AI Fundamentals L1 Badge from QuantumBox AI! 

🎯 Level 1 Certified in AI & Prompt Engineering
⭐ Verification: ${certificate.verification_code}

#AI #MachineLearning #PromptEngineering #QuantumBoxAI #L1Certified #DigitalBadge`;

    const shareUrl = window.location.origin + `/badge/${certificate.verification_code}`;

    // Create share options for different platforms
    const shareOptions = [
      {
        name: 'LinkedIn',
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent('QuantumBox AI L1 Badge')}&summary=${encodeURIComponent(shareText)}`
      },
      {
        name: 'Twitter',
        url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
      },
      {
        name: 'Facebook',
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`
      },
      {
        name: 'WhatsApp',
        url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`
      },
      {
        name: 'Telegram',
        url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
      }
    ];

    // Function to show custom share modal
    const showShareModal = () => {
      // Create a modal with share options
      const shareModal = document.createElement('div');
      shareModal.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
      shareModal.innerHTML = `
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
          <h3 class="text-lg font-semibold mb-4 text-center text-gray-900 dark:text-white">Share Badge</h3>
          <div class="space-y-2">
            ${shareOptions.map(option => `
              <button 
                class="w-full p-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600"
                onclick="window.open('${option.url}', '_blank', 'width=600,height=400'); document.body.removeChild(this.closest('.fixed'))"
              >
                📤 Share on ${option.name}
              </button>
            `).join('')}
            <button 
              class="w-full p-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600"
              onclick="navigator.clipboard.writeText('${shareText.replace(/'/g, "\\'")}'); 
                       const toast = document.createElement('div'); 
                       toast.textContent = 'Copied to clipboard!'; 
                       toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50'; 
                       document.body.appendChild(toast); 
                       setTimeout(() => document.body.removeChild(toast), 2000);
                       document.body.removeChild(this.closest('.fixed'))"
            >
              📋 Copy to Clipboard
            </button>
            <button 
              class="w-full p-3 text-center bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg transition-colors mt-4 text-gray-900 dark:text-white"
              onclick="document.body.removeChild(this.closest('.fixed'))"
            >
              Cancel
            </button>
          </div>
        </div>
      `;
      
      document.body.appendChild(shareModal);
      
      // Close modal when clicking outside
      shareModal.addEventListener('click', (e) => {
        if (e.target === shareModal) {
          document.body.removeChild(shareModal);
        }
      });
    };

    try {
      // Check if native share is available and try it
      if (navigator.share && navigator.canShare && navigator.canShare({ 
        title: 'QuantumBox AI L1 Badge',
        text: shareText,
        url: shareUrl
      })) {
        await navigator.share({
          title: 'QuantumBox AI L1 Badge',
          text: shareText,
          url: shareUrl
        });
      } else {
        // Fall back to custom share modal
        showShareModal();
      }
    } catch (error) {
      console.log('Native share not available, showing custom modal');
      // Always fall back to custom share modal on any error
      showShareModal();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading badge...</p>
        </CardContent>
      </Card>
    );
  }

  if (!certificate) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Award className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Badge Earned</h3>
           <p className="text-muted-foreground">
             Complete the course with a passing score of 80% to earn your L1 badge.
          </p>
        </CardContent>
      </Card>
    );
  }

  const issueDate = new Date(certificate.issued_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const recipientName = profile?.full_name || user?.user_metadata?.full_name || 'Student';

  return (
    <div className="space-y-6">
      {/* Digital Badge */}
      <div className="flex justify-center">
        <div 
          id="badge-content"
          className="relative w-80 h-80 animate-scale-in"
        >
          {/* Outer Badge Container */}
          <div 
            className="w-full h-full rounded-full shadow-2xl border-8 border-white bg-gradient-to-br from-purple-600 to-purple-800"
            style={{ 
              background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
              boxShadow: '0 20px 40px -8px rgba(147, 51, 234, 0.4)'
            }}
          >
            {/* Inner Content */}
            <div className="w-full h-full rounded-full bg-white flex flex-col items-center justify-center text-center p-8 relative">
              
              {/* Top Logo/Icon */}
              <div className="absolute top-6">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                  <Shield className="h-7 w-7 text-white" />
                </div>
              </div>

              {/* Course Title */}
              <div className="mt-12 mb-4">
                <h3 className="text-lg font-bold text-gray-800 leading-tight mb-1">
                  AI Fundamentals &
                </h3>
                <h3 className="text-lg font-bold text-gray-800 leading-tight">
                  Prompt Engineering
                </h3>
              </div>

              {/* Course Completed Badge */}
              <div className="bg-purple-600 text-white px-6 py-2 rounded-full font-bold text-sm mb-4 shadow-lg">
                COURSE COMPLETED
              </div>

              {/* Recipient Name */}
              <div className="mb-4">
                <p className="text-lg font-bold text-gray-800">
                  {recipientName}
                </p>
              </div>

              {/* Organization */}
              <div className="flex items-center gap-2 text-purple-600 font-semibold text-sm mb-2">
                <Award className="h-4 w-4" />
                <span>QUANTUMBOX AI</span>
              </div>

              {/* Date */}
              <div className="text-xs text-gray-600 font-medium">
                {issueDate}
              </div>

              {/* Verification Badge */}
              <div className="absolute bottom-4">
                <div className="bg-green-500 text-white text-xs px-4 py-1 rounded-full font-bold shadow-lg flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  VERIFIED
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Badge Actions */}
      <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
        <Button 
          onClick={generateBadgePNG}
          disabled={downloading}
          className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          <Download className="h-4 w-4 mr-2" />
          {downloading ? 'Generating...' : 'Download Badge'}
        </Button>
        <Button 
          variant="outline" 
          onClick={shareBadge}
          className="flex-1"
        >
          <Share2 className="h-4 w-4 mr-2" />
          Share Badge
        </Button>
      </div>

      {/* Badge Details */}
      <Card className="max-w-md mx-auto">
        <CardContent className="p-6 space-y-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2 flex items-center justify-center gap-2">
              <Award className="h-5 w-5 text-blue-600" />
              Level 1 AI Certification
            </h3>
            <p className="text-sm text-muted-foreground">
              Digital Badge • Verified Credential
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm font-medium">Recipient</span>
              <span className="text-sm">{recipientName}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm font-medium">Level</span>
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                L1 - Fundamentals
              </Badge>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm font-medium">Issued</span>
              <span className="text-sm">{issueDate}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm font-medium">Duration</span>
              <span className="text-sm">15 Hours</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium">Verification</span>
              <code className="text-xs bg-muted px-2 py-1 rounded">
                {certificate.verification_code}
              </code>
            </div>
          </div>
          
          <div className="pt-4 border-t text-center">
            <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
              <Check className="h-4 w-4" />
              <span className="text-sm font-medium">Verified Digital Credential</span>
            </div>
            <p className="text-xs text-muted-foreground">
              This badge represents verified completion of AI Fundamentals & Prompt Engineering course
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Skills Earned */}
      <Card className="max-w-md mx-auto">
        <CardContent className="p-6">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Star className="h-4 w-4 text-yellow-500" />
            Skills Demonstrated
          </h4>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">AI Fundamentals</Badge>
            <Badge variant="outline">Machine Learning</Badge>
            <Badge variant="outline">Prompt Engineering</Badge>
            <Badge variant="outline">Deep Learning</Badge>
            <Badge variant="outline">Neural Networks</Badge>
            <Badge variant="outline">AI Ethics</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};